import { useEffect, useMemo, useState } from "react";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router";
import { Check, X } from "lucide-react";
import { useStore } from "../../stores/rootStore";
import { Avatar } from "../ui/Avatar";
import { Spinner } from "../ui/Spinner";
import {
	friendToApiUser,
	resolveFriendBridgeAction,
	useFriendBridgeAction,
	type BridgeUser,
} from "../../lib/friendBridgeActions";
import type { FriendSummary } from "../../lib/social";

export const FriendBridgeModal = observer(() => {
	const action = useFriendBridgeAction();
	const { friends } = useStore();
	const navigate = useNavigate();

	useEffect(() => {
		if (!action) return;
		if (action.kind === "openRequests") {
			resolveFriendBridgeAction({ ok: true });
			void friends.refresh();
			navigate("/friends?tab=requests");
		}
		if (action.kind === "openProfile") {
			const friend = friends.friends.find((item) => item.userId === action.userId);
			if (!friend) {
				resolveFriendBridgeAction({ ok: false, reason: "profile_unavailable" });
				return;
			}
			resolveFriendBridgeAction({ ok: true });
			navigate(`/u/${friend.handle}`);
		}
	}, [action, friends, navigate]);

	if (!action || action.kind === "openRequests" || action.kind === "openProfile") return null;
	if (action.kind === "request") return <FriendRequestDialog user={action.user} />;
	return <FriendPickerDialog max={action.max} />;
});

const FriendRequestDialog = observer(({ user }: { user: BridgeUser }) => {
	const { friends } = useStore();
	const { t } = useTranslation();
	const [busy, setBusy] = useState(false);
	const handle = user.handle?.replace(/^@+/, "") ?? "";
	const close = () => {
		if (!busy) resolveFriendBridgeAction({ ok: false, reason: "cancelled" });
	};
	const confirm = async () => {
		if (!handle) {
			resolveFriendBridgeAction({ ok: false, reason: "profile_unavailable" });
			return;
		}
		setBusy(true);
		const ok = await friends.sendRequest(handle);
		setBusy(false);
		resolveFriendBridgeAction(ok ? { ok: true } : { ok: false, reason: friends.error || "request_failed" });
	};

	return (
		<ModalShell title={t("friends.addFriend")} onClose={close}>
			<div className="flex items-center gap-3 rounded-2xl bg-white/5 p-3">
				<Avatar name={user.displayName || handle || t("friends.user")} src={user.avatarUrl ?? null} size={52} />
				<div className="min-w-0">
					<div className="truncate text-sm font-black">{user.displayName || handle || t("friends.unknownUser")}</div>
					{handle && <div className="truncate text-xs text-muted">@{handle}</div>}
				</div>
			</div>
			<p className="mt-4 text-sm text-muted">{t("friends.bridgeRequestDesc")}</p>
			{friends.error && <p className="mt-3 text-sm text-danger">{friends.error}</p>}
			<div className="mt-5 grid grid-cols-2 gap-3">
				<button type="button" onClick={close} disabled={busy} className="rounded-full border border-white/10 px-4 py-2 text-sm font-bold text-muted hover:text-white disabled:opacity-60">
					{t("common.cancel")}
				</button>
				<button type="button" onClick={confirm} disabled={busy || !handle} className="btn-primary justify-center py-2 text-sm disabled:opacity-60">
					{busy && <Spinner size={16} />}
					{t("friends.sendRequest")}
				</button>
			</div>
		</ModalShell>
	);
});

const FriendPickerDialog = observer(({ max = 1 }: { max?: number }) => {
	const { friends } = useStore();
	const { t } = useTranslation();
	const [picked, setPicked] = useState<Set<string>>(() => new Set());
	const limit = Math.max(1, Math.min(20, max));

	useEffect(() => {
		if (friends.friends.length === 0) void friends.refresh();
	}, [friends]);

	const selected = useMemo(
		() => friends.friends.filter((friend) => picked.has(friend.userId)),
		[friends.friends, picked],
	);

	const close = () => resolveFriendBridgeAction({ ok: false, reason: "cancelled" });
	const confirm = () => {
		const users = selected.map(friendToApiUser);
		resolveFriendBridgeAction(limit === 1 ? (users[0] ?? null) : users);
	};

	return (
		<ModalShell title={t("friends.pickTitle")} onClose={close}>
			<p className="mb-3 text-sm text-muted">{t("friends.pickedCount", { picked: picked.size, limit })}</p>
			<div className="max-h-80 overflow-y-auto rounded-2xl border border-white/10">
				{friends.loading ? (
					<div className="grid h-40 place-items-center"><Spinner size={24} /></div>
				) : friends.friends.length === 0 ? (
					<div className="p-6 text-center text-sm text-muted">
						{t("friends.noFriendsYet")}<Link to="/friends" className="text-brand">{t("friends.goAdd")}</Link>
					</div>
				) : (
					friends.friends.map((friend) => (
						<FriendPickRow
							key={friend.userId}
							friend={friend}
							picked={picked.has(friend.userId)}
							onToggle={() => {
								setPicked((prev) => {
									const next = new Set(prev);
									if (next.has(friend.userId)) next.delete(friend.userId);
									else if (next.size < limit) next.add(friend.userId);
									return next;
								});
							}}
						/>
					))
				)}
			</div>
			<div className="mt-5 grid grid-cols-2 gap-3">
				<button type="button" onClick={close} className="rounded-full border border-white/10 px-4 py-2 text-sm font-bold text-muted hover:text-white">
					{t("common.cancel")}
				</button>
				<button type="button" onClick={confirm} disabled={selected.length === 0} className="btn-primary justify-center py-2 text-sm disabled:opacity-60">
					{t("common.confirm")}
				</button>
			</div>
		</ModalShell>
	);
});

function FriendPickRow({ friend, picked, onToggle }: { friend: FriendSummary; picked: boolean; onToggle: () => void }) {
	return (
		<button type="button" onClick={onToggle} className="flex w-full items-center gap-3 border-b border-white/10 px-3 py-3 text-left last:border-b-0 hover:bg-white/5">
			<Avatar name={friend.displayName || friend.handle} src={friend.avatarUrl} size={40} />
			<span className="min-w-0 flex-1">
				<span className="block truncate text-sm font-bold">{friend.displayName || friend.handle}</span>
				<span className="block truncate text-xs text-muted">@{friend.handle}</span>
			</span>
			<span className={`grid size-6 place-items-center rounded-full border ${picked ? "border-brand bg-brand text-white" : "border-white/20 text-transparent"}`}>
				<Check size={14} />
			</span>
		</button>
	);
}

function ModalShell({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
	return (
		<div className="fixed inset-0 z-50 grid place-items-center bg-black/70 px-4 backdrop-blur-sm">
			<div className="w-full max-w-md rounded-3xl border border-white/10 bg-[#171021] p-5 shadow-2xl">
				<div className="mb-4 flex items-center justify-between">
					<h2 className="text-xl font-black">{title}</h2>
					<button type="button" onClick={onClose} className="grid size-9 place-items-center rounded-full text-muted hover:bg-white/10 hover:text-white">
						<X size={18} />
					</button>
				</div>
				{children}
			</div>
		</div>
	);
}
